import { useEffect, useState } from "react";
import { Button, DatePicker, Select, Table, Typography, message } from "antd";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import locale from "antd/lib/date-picker/locale/es_ES";
import CollapsibleCard from "../../common/components/CollapsibleCard";
import useLoteNotificacionAPI, { ConfiguracionProduccionOption, ResumenLoteImpresion } from "../hooks/useLoteNotificacionAPI";

const { Text } = Typography;

const csvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;

const descargarCsv = (rows: ResumenLoteImpresion[]) => {
    const headers = ["Lote de impresión", "Fecha de envío", "Configuración de producción", "Total actas", "Enviadas", "No enviadas"];
    const body = rows.map((r) => [
        r.nroLote,
        r.fechaEnvio ? moment(r.fechaEnvio).format("DD/MM/YYYY HH:mm") : "",
        r.configuracionProduccionNombre ?? "",
        r.cantidadActasTotal,
        r.cantidadEnviadas,
        r.cantidadNoEnviadas,
    ].map(csvCell).join(";"));

    const csvContent = [headers.map(csvCell).join(";"), ...body].join("\n");
    // BOM UTF-8 al inicio para que Excel abra tildes/ñ correctamente (mismo enfoque que el resto de la app).
    const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `resumen-notificaciones-${moment().format("YYYYMMDD-HHmmss")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export default () => {
    const api = useLoteNotificacionAPI();
    const [configuraciones, setConfiguraciones] = useState<ConfiguracionProduccionOption[]>([]);
    const [fechaEnvioRange, setFechaEnvioRange] = useState<[Moment, Moment] | null>(null);
    const [configuracionProduccionId, setConfiguracionProduccionId] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [buscado, setBuscado] = useState(false);
    const [items, setItems] = useState<ResumenLoteImpresion[]>([]);

    useEffect(() => {
        api.listarConfiguracionesProduccion().then((res) => setConfiguraciones(res.data?.value ?? []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buscar = async () => {
        setLoading(true);
        const result = await api.obtenerResumenLotesImpresion({
            fechaEnvioDesde: fechaEnvioRange ? moment(fechaEnvioRange[0]).startOf("day").toISOString() : undefined,
            fechaEnvioHasta: fechaEnvioRange ? moment(fechaEnvioRange[1]).endOf("day").toISOString() : undefined,
            configuracionProduccionId,
        });
        setLoading(false);
        setBuscado(true);
        if (result.error) {
            message.error("Error al consultar el resumen de lotes");
            return;
        }
        setItems(result.data?.value ?? []);
    };

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <CollapsibleCard title="Filtros" style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div>
                        <div><Text strong>Fecha de envío</Text></div>
                        <DatePicker.RangePicker
                            style={{ marginTop: 8 }}
                            format="DD/MM/YYYY"
                            locale={locale}
                            value={fechaEnvioRange}
                            onChange={(range) => setFechaEnvioRange(range as [Moment, Moment] | null)}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 260 }}>
                        <div><Text strong>Configuración de producción</Text></div>
                        <Select
                            style={{ width: "100%", marginTop: 8 }}
                            placeholder="Todas"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            value={configuracionProduccionId}
                            onChange={setConfiguracionProduccionId}
                        >
                            {configuraciones.map((c) => (
                                <Select.Option key={c.Id} value={c.Id}>{c.Nombre}{c.UnidadProduccion ? `-${c.UnidadProduccion.Nombre}` : ""}</Select.Option>
                            ))}
                        </Select>
                    </div>
                    <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={buscar}>
                        Buscar
                    </Button>
                </div>
            </CollapsibleCard>

            <CollapsibleCard title="Lotes enviados">
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                    <Button icon={<DownloadOutlined />} disabled={!items.length} onClick={() => descargarCsv(items)}>
                        Descargar CSV
                    </Button>
                </div>
                <Table
                    rowKey="loteImpresionId"
                    loading={loading}
                    dataSource={items}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: buscado ? "Sin resultados para el filtro seleccionado" : "Elegí un filtro y presioná Buscar" }}
                >
                    <Table.Column title="Lote de impresión" dataIndex="nroLote" />
                    <Table.Column
                        title="Fecha de envío"
                        dataIndex="fechaEnvio"
                        render={(fecha?: string) => fecha ? moment(fecha).format("DD/MM/YYYY HH:mm") : "-"}
                    />
                    <Table.Column
                        title="Configuración de producción"
                        dataIndex="configuracionProduccionNombre"
                        render={(v?: string) => v ?? "-"}
                    />
                    <Table.Column title="Total actas" dataIndex="cantidadActasTotal" />
                    <Table.Column title="Enviadas" dataIndex="cantidadEnviadas" />
                    <Table.Column title="No enviadas" dataIndex="cantidadNoEnviadas" />
                </Table>
            </CollapsibleCard>
        </div>
    );
};
